import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '@/components/AppLayout';
import { useAuth } from '@/context/AuthContext';
import { API } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { toast } from 'sonner';
import {
  Calendar,
  AlertCircle,
  MessageSquare,
  Users,
  ChevronRight,
  Bell,
  TrendingUp,
  RefreshCw
} from 'lucide-react';

export default function DailySummary() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dailyData, setDailyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailSheetOpen, setDetailSheetOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailData, setDetailData] = useState({ title: '', items: [] });

  const fetchDailyData = useCallback(async () => {
    try {
      const response = await fetch(`${API}/daily`, { credentials: 'include' });
      if (response.ok) {
        setDailyData(await response.json());
      }
    } catch (error) {
      console.error('Error fetching daily data:', error);
      toast.error('Erro ao carregar resumo do dia');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDailyData();
  }, [fetchDailyData]);

  const openDetailSheet = async (detailType) => {
    setDetailLoading(true);
    setDetailSheetOpen(true);
    try {
      const response = await fetch(`${API}/dashboard/details?detail_type=${detailType}`, { credentials: 'include' });
      if (!response.ok) {
        throw new Error('Falha ao carregar detalhes');
      }
      setDetailData(await response.json());
    } catch (error) {
      toast.error('Erro ao carregar detalhes das conversas');
      setDetailSheetOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const getPriorityColor = (priority) => {
    return {
      high: 'bg-red-100 text-red-700 border-red-200',
      medium: 'bg-amber-100 text-amber-700 border-amber-200',
      low: 'bg-emerald-100 text-emerald-700 border-emerald-200'
    }[priority] || 'bg-zinc-100 text-zinc-700';
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="p-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 w-64 bg-zinc-200 rounded" />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="h-24 bg-zinc-200 rounded-xl" />
              ))}
            </div>
            <div className="h-64 bg-zinc-200 rounded-xl" />
          </div>
        </div>
      </AppLayout>
    );
  }

  const totalItems = (dailyData?.needs_response || 0) + (dailyData?.informational || 0);
  const responseRate = totalItems > 0 
    ? Math.round(((dailyData?.informational || 0) / totalItems) * 100) 
    : 0;
  const accountSegment = user?.account_segment === 'business' ? 'business' : 'personal';

  return (
    <AppLayout sandboxMode={dailyData?.sandbox_mode}>
      <div className="p-6 md:p-8 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-zinc-900 flex items-center gap-3">
              <Calendar className="w-8 h-8 text-violet-600" />
              Resumo do Dia
            </h1>
            <p className="text-zinc-500 mt-1 capitalize">
              {dailyData?.date ? formatDate(dailyData.date) : 'Carregando...'}
            </p>
            <p className="text-sm text-zinc-500 mt-1">
              {accountSegment === 'business'
                ? 'Leitura consolidada de clientes, equipe e operação no período.'
                : 'Leitura consolidada do que aconteceu nos seus grupos no período.'}
            </p>
          </div>
          
          <Button 
            onClick={fetchDailyData}
            variant="outline"
            className="rounded-full"
            data-testid="refresh-daily-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Atualizar
          </Button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card
            className="stat-card border-zinc-200 cursor-pointer hover:border-orange-300 transition-colors"
            onClick={() => openDetailSheet('needs_response')}
          >
            <CardContent className="p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-zinc-900">{dailyData?.groups_analyzed || 0}</p>
                  <p className="text-xs text-zinc-500">Grupos Analisados</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card border-zinc-200">
            <CardContent className="p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-violet-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-zinc-900">{dailyData?.total_messages || 0}</p>
                  <p className="text-xs text-zinc-500">Mensagens Totais</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card border-zinc-200">
            <CardContent className="p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-zinc-900">{dailyData?.needs_response || 0}</p>
                  <p className="text-xs text-zinc-500">Precisam Resposta</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card border-zinc-200">
            <CardContent className="p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-zinc-900">{responseRate}%</p>
                  <p className="text-xs text-zinc-500">Processados</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Alerts */}
        {dailyData?.alerts?.length > 0 && (
          <Card className="mb-8 border-orange-200 bg-orange-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Bell className="w-5 h-5 text-orange-600" />
                <div>
                  {dailyData.alerts.map((alert, index) => (
                    <p key={index} className="text-sm text-orange-700 font-medium">
                      {alert}
                    </p>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid lg:grid-cols-12 gap-6">
          {/* Top Priorities */}
          <div className="lg:col-span-7">
            <Card className="border-zinc-200">
              <CardHeader className="border-b border-zinc-100">
                <CardTitle className="flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-orange-600" />
                  Top 5 Respostas Prioritárias
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                {dailyData?.top_priorities?.length > 0 ? (
                  <div className="space-y-4">
                    {dailyData.top_priorities.map((item, index) => (
                      <div 
                        key={index}
                        className="p-4 bg-zinc-50 rounded-lg border border-zinc-200 cursor-pointer hover:border-violet-300 transition-colors"
                        onClick={() => navigate(`/app/groups/${item.group_id}`)}
                        data-testid={`priority-item-${index}`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2">
                              <Badge variant="outline" className="text-xs bg-zinc-100 border-0">
                                {item.group_name}
                              </Badge>
                              <Badge className={`text-xs border ${getPriorityColor(item.priority)}`}>
                                {item.priority === 'high' ? 'Alta' : item.priority === 'medium' ? 'Média' : 'Baixa'}
                              </Badge>
                            </div>
                            <p className="text-zinc-900 font-medium line-clamp-2">{item.question}</p>
                            <p className="text-xs text-zinc-500 mt-1">De: {item.sender}</p>
                          </div>
                          <ChevronRight className="w-5 h-5 text-zinc-400 flex-shrink-0" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-zinc-500">
                    <AlertCircle className="w-10 h-10 mx-auto mb-3 text-zinc-300" />
                    <p>Nenhuma prioridade identificada</p>
                    <p className="text-sm mt-1">Gere resumos nos grupos para ver prioridades</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Group Summaries */}
          <div className="lg:col-span-5">
            <Card className="border-zinc-200">
              <CardHeader className="border-b border-zinc-100">
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-violet-600" />
                  Resumos por Grupo
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                {dailyData?.summaries?.length > 0 ? (
                  <div className="space-y-3">
                    {dailyData.summaries.map((summary, index) => (
                      <div 
                        key={index}
                        className="p-3 bg-zinc-50 rounded-lg cursor-pointer hover:bg-zinc-100 transition-colors"
                        onClick={() => navigate(`/app/groups/${summary.group_id}`)}
                      >
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <p className="text-sm font-medium text-zinc-900 line-clamp-1">
                            {summary.group_name || 'Grupo'}
                          </p>
                          {summary.has_real_summary === false ? (
                            <Badge className="bg-amber-100 text-amber-700 border-0 text-[10px] px-2 py-0">
                              Sem resumo
                            </Badge>
                          ) : null}
                        </div>
                        <p className="text-xs text-zinc-500 line-clamp-2">
                          {summary.one_liner}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-zinc-500">
                    <MessageSquare className="w-8 h-8 mx-auto mb-2 text-zinc-300" />
                    <p className="text-sm">Nenhum resumo gerado ainda</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Progress Overview */}
            <Card className="border-zinc-200 mt-6">
              <CardHeader className="border-b border-zinc-100">
                <CardTitle className="text-base">Progresso do Dia</CardTitle>
              </CardHeader>
              <CardContent className="p-4 space-y-4">
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-zinc-600">Mensagens Processadas</span>
                    <span className="font-medium text-zinc-900">{responseRate}%</span>
                  </div>
                  <Progress value={responseRate} className="h-2" />
                </div>
                
                <div className="grid grid-cols-2 gap-4 pt-2">
                  <div className="text-center p-3 bg-emerald-50 rounded-lg">
                    <p className="text-2xl font-bold text-emerald-600">
                      {dailyData?.informational || 0}
                    </p>
                    <p className="text-xs text-emerald-700">Informativos</p>
                  </div>
                  <div className="text-center p-3 bg-orange-50 rounded-lg">
                    <p className="text-2xl font-bold text-orange-600">
                      {dailyData?.needs_response || 0}
                    </p>
                    <p className="text-xs text-orange-700">Pendentes</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        <Sheet open={detailSheetOpen} onOpenChange={setDetailSheetOpen}>
          <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto">
            <SheetHeader>
              <SheetTitle>{detailData.title || 'Detalhes'}</SheetTitle>
              <SheetDescription>
                Conversas relacionadas ao indicador selecionado no resumo do dia.
              </SheetDescription>
            </SheetHeader>

            <div className="mt-6 space-y-3">
              {detailLoading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((item) => (
                    <div key={item} className="h-24 rounded-xl bg-zinc-100 animate-pulse" />
                  ))}
                </div>
              ) : detailData.items?.length > 0 ? (
                detailData.items.map((item) => (
                  <button
                    key={item.message_id}
                    type="button"
                    onClick={() => navigate(`/app/groups/${item.group_id}`)}
                    className="w-full rounded-xl border border-zinc-200 bg-white p-4 text-left hover:border-violet-300 hover:bg-violet-50/30 transition-colors"
                  >
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <div>
                        <p className="font-medium text-zinc-900">{item.group_name}</p>
                        <p className="text-xs text-zinc-500">
                          {item.sender} • {item.date ? new Date(`${item.date}T12:00:00`).toLocaleDateString('pt-BR') : '-'}
                        </p>
                      </div>
                      <Badge className={`${item.needs_action ? 'bg-orange-100 text-orange-700' : 'bg-emerald-100 text-emerald-700'} border-0`}>
                        {item.action_label}
                      </Badge>
                    </div>
                    <p className="text-sm text-zinc-700 line-clamp-3">{item.content}</p>
                  </button>
                ))
              ) : (
                <div className="rounded-xl border border-dashed border-zinc-300 p-6 text-center text-zinc-500">
                  Nenhuma conversa encontrada neste indicador.
                </div>
              )}
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </AppLayout>
  );
}
